import React from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import { ProjectLayoutModel } from '@paragon/contract-library';
import { editLayoutModelProperties } from '@paragon/layout-models';
import {
  getOrganizationMemberIfLoaded,
  getOrganizationMembers,
  getOrganizationMembersStatus,
  getUnknownUser,
  getUserDisplayName,
  NamedUser,
} from '@paragon/login-tools';

import { UserAvatar } from './AssigneeAvatar';
import { useAppDispatch, useAppSelector } from './hooks';

interface Props {
  readonly project: ProjectLayoutModel;
}

/**
 * Chooses the organization member responsible for a project.
 */
export function AssigneeSelect({ project }: Props) {
  const dispatch = useAppDispatch();

  const members = useAppSelector(getOrganizationMembers);
  const status = useAppSelector(getOrganizationMembersStatus);
  const selectedMember = useAppSelector((state) =>
    project.assigneeEmail == null ? undefined : getOrganizationMemberIfLoaded(state, project.assigneeEmail),
  );

  const unknownAssignee = project.assigneeEmail && !selectedMember ? getUnknownUser(project.assigneeEmail) : null;

  const options: readonly NamedUser[] = unknownAssignee ? [unknownAssignee, ...members] : members;

  return (
    <Autocomplete<NamedUser>
      options={options}
      value={selectedMember ?? unknownAssignee}
      loading={status === 'loading'}
      disabled={status === 'error' && !unknownAssignee}
      size="small"
      autoHighlight
      isOptionEqualToValue={(option, value) => option.email === value.email}
      getOptionLabel={getUserDisplayName}
      renderOption={({ key, ...optionProps }, option) => {
        const displayName = getUserDisplayName(option);

        return (
          <Box component="li" key={option.email} {...optionProps} sx={{ display: 'flex', gap: 1 }}>
            <UserAvatar user={option} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {displayName}
              </Typography>
              {displayName !== option.email && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {option.email}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Assignee"
          placeholder={status === 'error' ? 'Members unavailable' : 'Unassigned'}
        />
      )}
      onChange={(_, member) =>
        dispatch(editLayoutModelProperties(project.guid, { assigneeEmail: member?.email ?? null }))
      }
    />
  );
}
